import React, { useEffect, useMemo, useState } from 'react';
import { Card, Spinner, Table } from 'react-bootstrap';
import { useFirestore } from '../contexts/FirestoreContext';

// --- helpers ---
const toDate = (ts) => (ts && typeof ts.toDate === 'function' ? ts.toDate() : (ts instanceof Date ? ts : null));
const ageInDays = (d) => d == null ? null : Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
const twoDigitYear = (y) => {
  if (y == null) return '';
  const s = String(y);
  return s.length >= 2 ? s.slice(-2) : s;
};
const stockDisplay = (v) =>
  v?.strStockNbr ? v.strStockNbr
    : (v?.strVin && v.strVin.length >= 8) ? v.strVin.slice(-8)
      : (v?.strVin || '');

const resolveModelName = (v, modelCodes = {}) => {
  const factory = (v?.strFactory ?? v?.strMake ?? '').trim();
  const factory2 = factory.toLowerCase().includes('ford') ? 'Ford' : factory.toLowerCase().includes('gm') ? 'GM' : factory;
  const model = (v?.strModel ?? '').trim();
  if (!factory && !model) return '';
  const k1 = `${factory2} - ${model}`;
  const k2 = `${factory2} - ${model.substring(0, 3)}`;
  return modelCodes[k1] || modelCodes[k2] || model || '';
};

const titleDate = () => {
  const d = new Date();
  return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
};

// --- component ---
export default function OldestVehiclesReport({ onlyStock = true, perMakeCount = 10 }) {
  const { onSnapshotData, onSnapshotQueryData, getData } = useFirestore();
  const [vehicles, setVehicles] = useState([]);
  const [salesOrders, setSalesOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [modelCodes, setModelCodes] = useState({});
  const [loading, setLoading] = useState(true);

  // vehicles (stock only by default)
  useEffect(() => {
    const unsub = onlyStock
      ? onSnapshotQueryData('vehicles', 'strSalesOrdNbr', '==', 'Stock', (data) => { setVehicles(data || []); setLoading(false); })
      : onSnapshotData('vehicles', (data) => { setVehicles(data || []); setLoading(false); });
    return () => unsub && unsub();
  }, [onlyStock, onSnapshotData, onSnapshotQueryData]);

  // sales orders & customers (for Customer name lookup)
  useEffect(() => {
    const u1 = onSnapshotData('salesOrders', setSalesOrders);
    const u2 = onSnapshotData('customers', setCustomers);
    return () => { u1 && u1(); u2 && u2(); };
  }, [onSnapshotData]);

  // model code map
  useEffect(() => {
    (async () => {
      try {
        const data = await getData('modelCodes');
        const map = (data || []).reduce((acc, obj) => {
          const make = obj.strFactory || obj.strMake || '';
          acc[`${make} - ${obj.strModel}`] = obj.strModelName;
          return acc;
        }, {});
        setModelCodes(map);
      } catch {/* ignore */ }
    })();
  }, [getData]);

  const getCustomerName = (vehicle) => {
    // Use the vehicle's sales order to find the customer name (works for "Stock" too if you have a "Hardy Stock Vehicles" customer)
    const so = salesOrders.find(o => o.strSalesOrdNbr === vehicle.strSalesOrdNbr);
    if (!so) return '';
    const cust = customers.find(c => c.strCustNbr === so.strCustNbr);
    return cust?.strCustName || '';
  };

  const { ford, gm } = useMemo(() => {
    // include only vehicles with dtRecvDate
    const withRecv = (vehicles || []).filter(v => !!toDate(v.dtRecvDate));

    const decorated = withRecv.map(v => {
      const d = toDate(v.dtRecvDate);
      return {
        ...v,
        _age: ageInDays(d),
        _factory: (v.strFactory || v.strMake || '').toLowerCase().includes('ford') ? 'Ford' : 'GM',
      };
    });

    const pickTop = (factory) =>
      decorated
        .filter(v => v._factory === factory)
        .sort((a, b) => (b._age ?? -1) - (a._age ?? -1))
        .slice(0, perMakeCount);

    return {
      ford: pickTop('Ford'),
      gm: pickTop('GM'),
    };
  }, [vehicles, perMakeCount]);

  if (loading) {
    return (
      <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 240 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  const Section = ({ title, rows }) => (
    <>
      <tr>
        <td colSpan={6} className="fw-bold pt-3">{title}</td>
      </tr>
      {rows.map(v => (
        <tr key={v.id}>
          <td>{stockDisplay(v)}</td>
          <td>{getCustomerName(v)}</td>
          <td>{v.strSalesOrdNbr || ''}</td>
          <td>{twoDigitYear(v.strYR)}</td>
          <td>{resolveModelName(v, modelCodes)}</td>
          <td>{v._age ?? ''}</td>
        </tr>
      ))}
      {rows.length === 0 && (
        <tr><td colSpan={6} className="text-muted">No vehicles found.</td></tr>
      )}
    </>
  );

  return (
    <Card>
      <Card.Body>
        <div className="text-center mb-3">
          <div className="fw-bold">Hardy Fleet Group</div>
          <div>Inventory Age Report</div>
          <div className="fw-bold">Oldest 10 Vehicles</div>
          <div className="fw-bold">{titleDate()}</div>
        </div>

        <Table bordered responsive>
          <thead>
            <tr>
              <th>Stock #</th>
              <th>Customer</th>
              <th>SO #</th>
              <th>Yr</th>
              <th>Model</th>
              <th>Age</th>
            </tr>
          </thead>
          <tbody>
            <Section title="Ford" rows={ford} />
            <tr><td colSpan={6} style={{ border: 0, height: 12 }}></td></tr>
            <Section title="GM" rows={gm} />
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
}
